import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { atomFamily } from "jotai-family";

// ============================================================================
// User preferences (persisted)
// ============================================================================

/**
 * Fill opacity (alpha) for segmentation mask contours in the LABELING viewer,
 * personalizable via the toolbar slider and persisted to localStorage.
 *
 * Lower than the demo viewer's 0.35 by default so the underlying anatomy stays
 * visible while annotating. The labeling viewer is the sole writer of the
 * global contour `fillAlpha` (it set none before), and restores the prior style
 * on unmount, so this never bleeds into the demo viewer (separate route).
 *
 * Range surfaced in the UI is 0–0.6; 0 keeps the outline but hides the fill.
 *
 * `getOnInit: true` so the persisted value is read on first render — without it
 * the apply-effect would briefly apply the 0.2 default before storage hydrates,
 * flashing the mask opacity on every case load.
 */
export const maskFillAlphaAtom = atomWithStorage<number>("labelingMaskFillAlpha", 0.2, undefined, {
  getOnInit: true,
});

// ============================================================================
// Types
// ============================================================================

/** DICOM plane geometry for world-space coordinate conversion. */
export interface PlaneContourData {
  contours: Record<string, number[][][]>;
  /**
   * Parallel to `contours`: per-slice array of booleans, one per contour at
   * that slice. `true` means the contour was auto-generated (e.g. companion
   * propagation, axial-propagation) and should render as a dashed ring in 3D.
   * Missing entries default to false (solid).
   */
  autoGenerated?: Record<string, boolean[]>;
  spacing: [number, number, number];
  dimensions: [number, number, number];
  origin: [number, number, number];
  rowCosines: [number, number, number];
  columnCosines: [number, number, number];
  sliceDirection: [number, number, number];
}

export interface AnnotationRecord {
  annotation_id: string;
  project_id: string;
  study_instance_uid: string;
  series_instance_uid: string;
  annotator: string;
  annotated_at: string;
  segment_index: number;
  category: string;
  annotation_type: "segmentation";
  mask_s3_path: string;
  segmentation_stats?: {
    slice_count?: number;
    volume_mm3?: number;
    hu_min?: number;
    hu_max?: number;
    hu_avg?: number;
  } | null;
  /** Convex-hull stats from the contour points. Sidebar prefers this over
   *  segmentation_stats.volume_mm3 because contour-only annotations leave
   *  the voxel mask sparse → mask volume rounds to 0 even when the annotation
   *  is sized correctly. The 3D viewer overlay shows the same hull volume,
   *  so reading it here makes the sidebar match the viewer. */
  hull_stats?: {
    volume_mm3: number;
    axis_1_mm: number;
    axis_2_mm: number;
    axis_3_mm: number;
  } | null;
  measurements: {
    long_axis_mm: number;
    short_axis_mm: number;
  };
  bounding_box?: {
    point1_k: number;
    point2_k: number;
    [key: string]: number;
  };
  attributes: Record<string, string | boolean | number | string[]>;
  timing: {
    start_ts: string;
    end_ts: string;
    duration_seconds: number;
    /** Idle-excluded active engagement time; absent on legacy records. */
    active_seconds?: number;
  };
  source: "cornerstone";
}

// Measurements derived client-side before save (pre-flight estimates)
export interface DerivedMeasurements {
  long_axis_mm: number;
  short_axis_mm: number;
  volume_mm3: number;
  hu_min: number;
  hu_max: number;
  hu_avg: number;
  slice_count: number;
}

// ============================================================================
// Active segment state
// ============================================================================

// Which segment index is currently being drawn (1-based; 0 = none active)
export const activeSegmentIndexAtom = atom<number>(1);

// Whether a segmentation is currently being drawn (used to record start_ts)
export const isDrawingAtom = atom<boolean>(false);

// Timestamp when annotator first touched the brush for the current instance
export const annotationStartTsAtom = atom<string | null>(null);

// Derived measurements for the current in-progress segment (shown in form pre-save)
export const derivedMeasurementsAtom = atom<DerivedMeasurements | null>(null);

// Cornerstone segmentation volume ID for the current study (set after volume loads)
export const segmentationVolumeIdAtom = atom<string | null>(null);

/**
 * Unified viewport path (default; `?newViewport=0` opts back to legacy). Each
 * per-pane `SessionVolumeMount` publishes its derived labelmap segmentation id
 * here (keyed by `seriesId`) once that pane's labelmap volume + segmentation
 * are registered; null = not ready / torn down.
 *
 * The layout-level segmentation owner in `HangingProtocolGrid` reads these to
 * learn when the chosen (priority) series' segmentation is ready, so it can
 * pre-initialize segment metadata and set `segmentationVolumeIdAtom` ONCE,
 * deterministically — instead of every pane racing to set the gate
 * (last-writer-wins), which let the active contour seg / its initialized
 * segments disagree with the gate (the saved-mask `'locked'` restore crash and
 * the draw segmentIndex desync on the new path). Untouched by the old/default
 * path: only `SessionVolumeMount` (flag-on) ever writes it.
 */
export const seriesSegReadyFamily = atomFamily((_seriesId: string) => atom<string | null>(null));

// ============================================================================
// Saved annotation list for the current study
// ============================================================================

// Annotations already saved to the backend for the current study+project
export const savedAnnotationsAtom = atom<AnnotationRecord[]>([]);

// Which saved annotation is selected in the panel (annotation_id or null)
export const selectedAnnotationIdAtom = atom<string | null>(null);

// ============================================================================
// Viewport locking — once drawing starts, lock to the series being annotated
// ============================================================================

// Whether user has started an annotation workflow (clicked Add + selected category)
export const isSegmentationActiveAtom = atom<boolean>(false);

// Signal atom: when a segmentation hotkey is pressed without an active annotation,
// write the desired tool here. SegmentationAnnotationPanel reacts by auto-creating an
// annotation and then activating the requested tool.
export const requestAnnotationStartAtom = atom<"lasso" | "bump" | "regionGrow" | null>(null);
export const saveAnnotationRequestAtom = atom(0);

// Which series is locked for the current annotation (null = not locked yet).
// Derived at lock time from the annotation's referencedImageId — no closures.
export const lockedSeriesIdAtom = atom<string | null>(null);

// Whether the first stroke has been made (controls skip button visibility)
export const hasFirstStrokeAtom = atom<boolean>(false);

// Incremented when a contour is rejected on a non-locked series (triggers warning toast)
export const seriesLockWarningAtom = atom<number>(0);

// Bumps every time the contour set changes (stroke drawn, save, delete,
// region-grow commit). Subscribers that derive from cornerstone keyframe
// state (e.g. SegmentationViewer3D) re-read on each bump.
export const contourVersionAtom = atom<number>(0);

// ============================================================================
// Bump tool state
// ============================================================================

/** Available bump brush radii in screen pixels (XS → L) */
export const BUMP_RADII = [6, 8, 16, 28, 44] as const;
export type BumpRadius = (typeof BUMP_RADII)[number];

/** Currently selected bump brush radius (screen pixels) */
export const bumpRadiusAtom = atom<BumpRadius>(28);

// ============================================================================
// Segmentation Assist Config (V2 tool behavior per annotation type)
// ============================================================================

import type { SegmentationAssistConfig } from "../hooks/useLabelingQueries";

/**
 * Segmentation assist config for the currently active annotation type.
 *
 * Set by SegmentationAnnotationPanel when the annotator selects a category.
 * Read by tool components to gate V2 tool availability and configure
 * HU parameters. Null means config hasn't loaded yet — V2 tools are
 * unavailable (no fallback).
 */
export const segmentationAssistAtom = atom<SegmentationAssistConfig | null>(null);

/** Whether an nnInteractive session is currently active (has at least one session). */
export const nnSessionActiveAtom = atom<boolean>(false);

/**
 * User-selected segmentation engine for region-grow clicks.
 * - "nnInteractive": GPU model (default), falls back to threshold on failure.
 * - "threshold": local FWHM-adaptive flood fill, no GPU call.
 * First session defaults to nnInteractive; subsequent sessions remember the
 * user's last choice via localStorage.
 */
export type SegEngine = "nnInteractive" | "threshold";
export const segEngineAtom = atomWithStorage<SegEngine>("segEngine.v2", "nnInteractive");

/**
 * True while the segmentation reader study is open (ENG-188). The nnInteractive
 * model checkpoint is licensed for non-commercial use only, so region grow uses
 * the local threshold and no nnInteractive request is sent, whatever
 * `segEngineAtom` holds. Not persisted: other viewers start unblocked.
 */
export const nnInteractiveBlockedAtom = atom<boolean>(false);

/**
 * True while the segmentation reader study is open (ENG-280). A study saves one
 * record per stone on one series: region grow doesn't propagate to the other
 * series' panes, and Save sends no companion labelmaps. Not persisted.
 */
export const companionMasksBlockedAtom = atom<boolean>(false);

/**
 * Attribute form density. "light" (default) shows only each category's quick-
 * capture (`light`) fields; "full" reveals the complete field set. Persisted as
 * an annotator preference; toggled from the annotation panel.
 */
export type AttributeViewMode = "light" | "full";
export const attributeViewModeAtom = atomWithStorage<AttributeViewMode>(
  "labelingAttributeViewMode",
  "light",
);

/**
 * Click markers for nnInteractive refinement (#203).
 * Green (positive) / red (negative) dots shown at each click point.
 * Cleared on undo/reject.
 */
export interface NnClickMarker {
  worldPoint: [number, number, number];
  label: "positive" | "negative";
  viewportId: string;
  /** Image ID of the slice where the click occurred — dot only renders on this slice. */
  imageId: string;
}
export const nnClickMarkersAtom = atom<NnClickMarker[]>([]);

/**
 * nnInteractive loading state.
 * Set by RegionGrowTool when an API call is in flight.
 * Read by VolumeViewport to show a spinner overlay on the target viewport.
 */
export const nnInteractiveLoadingAtom = atom<{
  isLoading: boolean;
  viewportId: string | null;
  /** Viewport IDs where companion series are loading (show spinners). */
  companionViewportIds?: string[];
}>({ isLoading: false, viewportId: null });

/**
 * nnInteractive segmentation result for 3D viewer.
 * Updated after each successful nnInteractive call.
 */
export const nnSegmentationResultAtom = atom<{
  /** Axial contours: z_idx -> [[y,x], ...] */
  contours: Record<string, number[][][]>;
  /** Parallel to contours; true entries render dashed in 3D. */
  autoGenerated?: Record<string, boolean[]>;
  /** Volume spacing [z, y, x] in mm */
  spacing: [number, number, number];
  /** Volume dimensions [z, y, x] in voxels */
  dimensions: [number, number, number];
  /** Volume origin [x, y, z] in world coords */
  origin: [number, number, number];
  volume_mm3: number;
  max_diameter_mm: number;
  trigger: number;
  /** Axial DICOM orientation for proper world-space contour rendering */
  rowCosines: [number, number, number];
  columnCosines: [number, number, number];
  sliceDirection: [number, number, number];
  /** Per-plane contours from companion series for multi-plane 3D rendering */
  planeContours?: {
    coronal?: PlaneContourData;
    sagittal?: PlaneContourData;
  };
} | null>(null);
